import { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { seriesAPI } from '../../services/api';
import { PageHeader, StatCards, SectionTitle, Field, Spinner, EmptyState } from '../../components/ui';
import api from '../../services/api';
import toast from 'react-hot-toast';

// ─── CSV column aliases ───────────────────────────────────────────────────────
const COL_MAP = {
  team_name: ['team', 'team name', 'club', 'name'],
  played:    ['p', 'played', 'mp', 'matches played', 'pld'],
  won:       ['w', 'won', 'wins'],
  lost:      ['l', 'lost', 'losses'],
  tied:      ['t', 'tied', 'tie'],
  no_result: ['nr', 'no result', 'n/r', 'n.r.'],
  points:    ['pts', 'points', 'pt'],
  nrr:       ['nrr', 'net run rate', 'run rate', 'net rr'],
};

function guessCol(headers, field) {
  return headers.find(h => COL_MAP[field].includes(h.toLowerCase().trim())) || null;
}

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (raw.length < 2) return reject(new Error('File appears empty'));
        let headerIdx = 0;
        for (let i = 0; i < Math.min(5, raw.length); i++) {
          if (raw[i].filter(c => String(c).trim()).length > 2) { headerIdx = i; break; }
        }
        const headers = raw[headerIdx].map(h => String(h).trim());
        const rows = raw.slice(headerIdx + 1)
          .filter(r => r.some(c => String(c).trim()))
          .map(r => { const obj = {}; headers.forEach((h, i) => { obj[h] = String(r[i] || '').trim(); }); return obj; });
        resolve({ headers, rows });
      } catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsArrayBuffer(file);
  });
}

function mapRows(rows, headers) {
  const colFor = {};
  Object.keys(COL_MAP).forEach(f => { colFor[f] = guessCol(headers, f); });
  return rows.map(r => ({
    team_name: r[colFor.team_name] || '',
    played:    r[colFor.played]    || '',
    won:       r[colFor.won]       || '',
    lost:      r[colFor.lost]      || '',
    tied:      r[colFor.tied]      || '',
    no_result: r[colFor.no_result] || '',
    points:    r[colFor.points]    || '',
    nrr:       r[colFor.nrr]       || '',
  })).filter(r => r.team_name);
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function CricClubsPage() {
  const [seriesList, setSeriesList]   = useState([]);
  const [localSeriesId, setLocalSeries] = useState('');
  const [syncHistory, setSyncHistory] = useState([]);
  const [statusInfo, setStatusInfo]   = useState(null);
  const [activeTab, setActiveTab]     = useState('import');

  useEffect(() => {
    seriesAPI.list().then(r => {
      setSeriesList(r.data);
      if (r.data.length > 0) setLocalSeries(r.data[0].id);
    }).catch(() => {});
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/cricclubs/status');
      setStatusInfo(data);
      setSyncHistory(data.history || []);
    } catch {}
  };

  // Only count successful CSV imports for stat cards
  const csvHistory = syncHistory.filter(h => h.method === 'csv');
  const lastImport = csvHistory[0];
  const cards = [
    { label: 'Last Import',   value: lastImport ? new Date(lastImport.completed_at || lastImport.started_at).toLocaleDateString() : 'Never', sub: 'standings CSV', color: 'var(--accent)' },
    { label: 'Records',       value: lastImport?.records_synced ?? '—', sub: 'last import', color: 'var(--blue)' },
    { label: 'Status',        value: lastImport?.status || '—', sub: 'last run', color: lastImport?.status === 'success' ? 'var(--accent)' : lastImport?.status === 'partial' ? 'var(--gold)' : lastImport ? 'var(--red)' : 'var(--dim)' },
    { label: 'Total Imports', value: csvHistory.length, sub: 'all time', color: 'var(--purple)' },
  ];

  const TABS = [
    ['import',  '↓ Import Standings'],
    ['api',     '⚡ API Sync'],
    ['history', 'History'],
    ['settings','⚙ Settings'],
  ];

  return (
    <div>
      <PageHeader icon="🏏" title="CricClubs" subtitle="Import standings and sync data with CricClubs" />
      <StatCards cards={cards} />

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {TABS.map(([t, label]) => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: activeTab === t ? 700 : 500,
            color: activeTab === t ? 'var(--accent)' : 'var(--mid)',
            borderBottom: activeTab === t ? '2px solid var(--accent)' : '2px solid transparent',
            fontSize: 13, marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>

      {activeTab === 'import'  && <ImportTab seriesList={seriesList} localSeriesId={localSeriesId} setLocalSeries={setLocalSeries} onDone={loadStatus} />}
      {activeTab === 'api'     && <ApiTab seriesList={seriesList} statusInfo={statusInfo} />}
      {activeTab === 'history' && <HistoryTab syncHistory={syncHistory} />}
      {activeTab === 'settings'&& <SettingsTab seriesList={seriesList} />}
    </div>
  );
}

// ─── Import Tab ───────────────────────────────────────────────────────────────
function ImportTab({ seriesList, localSeriesId, setLocalSeries, onDone }) {
  const [parsedRows, setParsedRows]     = useState([]);
  const [headers, setHeaders]           = useState([]);
  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImportResult(null);
    try {
      const { headers: h, rows } = await parseFile(f);
      setHeaders(h);
      setParsedRows(mapRows(rows, h));
      toast.success(`Parsed ${rows.length} rows from ${f.name}`);
    } catch (err) { toast.error(err.message); }
  };

  const handleImport = async () => {
    if (!localSeriesId) return toast.error('Select a series first');
    if (!parsedRows.length) return toast.error('No rows to import');
    setImporting(true);
    try {
      const { data } = await api.post('/cricclubs/import/standings', { local_series_id: localSeriesId, rows: parsedRows });
      setImportResult(data);
      if (data.matched > 0) toast.success(`Imported ${data.matched} team standings`);
      if (data.unmatched?.length) toast(`${data.unmatched.length} teams unmatched`, { icon: '⚠️' });
      onDone();
      setParsedRows([]);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) { toast.error(err.response?.data?.error || 'Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <SectionTitle>Import Standings from CSV / Excel</SectionTitle>
        <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20 }}>
          Export the standings table from CricClubs, then upload here. The import matches teams by name and updates standings automatically.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <Field label="Series" required>
            <select className="input select" value={localSeriesId} onChange={e => setLocalSeries(e.target.value)}>
              <option value="">Select series…</option>
              {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.season_year})</option>)}
            </select>
          </Field>
          <Field label="File" note="CSV or Excel (.csv, .xlsx, .xls)">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileChange}
              style={{ display: 'block', width: '100%', padding: '8px 0', fontSize: 13, color: 'var(--text)' }} />
          </Field>
        </div>

        <div style={{ padding: '12px 16px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mid)', marginBottom: 8 }}>HOW TO GET THE FILE FROM CRICCLUBS</div>
          <ol style={{ fontSize: 12, color: 'var(--mid)', paddingLeft: 18, lineHeight: 2 }}>
            <li>Go to <strong>cricclubs.com/MichCA</strong> → your series → Standings</li>
            <li>Select all the table text, paste into Excel, save as <strong>.xlsx</strong></li>
            <li>Required columns: <code>Team, P, W, L, Pts</code> — NRR and Tied are optional</li>
          </ol>
        </div>

        {parsedRows.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <SectionTitle color="var(--blue)">Preview — {parsedRows.length} rows</SectionTitle>
            <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
              <table className="data-table" style={{ fontSize: 12 }}>
                <thead><tr>{['#','Team','P','W','L','T','NR','Pts','NRR'].map(h => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {parsedRows.slice(0, 20).map((r, i) => (
                    <tr key={i}>
                      <td style={{ color: 'var(--dim)' }}>{i+1}</td>
                      <td style={{ fontWeight: 600 }}>{r.team_name}</td>
                      <td>{r.played||'—'}</td>
                      <td style={{ color: 'var(--accent)' }}>{r.won||'—'}</td>
                      <td style={{ color: 'var(--red)' }}>{r.lost||'—'}</td>
                      <td>{r.tied||'—'}</td>
                      <td>{r.no_result||'—'}</td>
                      <td style={{ fontWeight: 700 }}>{r.points||'—'}</td>
                      <td style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11 }}>{r.nrr||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-primary" onClick={handleImport} disabled={importing || !parsedRows.length || !localSeriesId}>
            {importing ? <><Spinner size={14} /> Importing…</> : `↓ Import ${parsedRows.length || ''} Standings`}
          </button>
          {parsedRows.length > 0 && (
            <button className="btn btn-ghost" onClick={() => { setParsedRows([]); setImportResult(null); if (fileRef.current) fileRef.current.value = ''; }}>✕ Clear</button>
          )}
        </div>
      </div>

      {importResult && (
        <div className="card" style={{ padding: 20 }}>
          <SectionTitle color={importResult.unmatched?.length ? 'var(--gold)' : 'var(--accent)'}>Import Result</SectionTitle>
          <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
            <div><span style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)' }}>{importResult.matched}</span> <span style={{ fontSize: 13, color: 'var(--mid)' }}>matched & updated</span></div>
            {importResult.unmatched?.length > 0 && <div><span style={{ fontSize: 28, fontWeight: 800, color: 'var(--gold)' }}>{importResult.unmatched.length}</span> <span style={{ fontSize: 13, color: 'var(--mid)' }}>not found</span></div>}
          </div>
          {importResult.unmatched?.length > 0 && (
            <div style={{ padding: '10px 14px', background: 'rgba(245,166,35,.07)', border: '1px solid rgba(245,166,35,.25)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', marginBottom: 6 }}>UNMATCHED — check name matches Teams page:</div>
              {importResult.unmatched.map(u => <div key={u} style={{ fontSize: 12, color: 'var(--mid)', lineHeight: 1.8 }}>• {u}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── API Sync Tab ─────────────────────────────────────────────────────────────
function ApiTab({ seriesList, statusInfo }) {
  const [seriesId, setSeriesId]       = useState('');
  const [ccSeriesId, setCcSeriesId]   = useState('');
  const [testing, setTesting]         = useState(false);
  const [pulling, setPulling]         = useState(false);
  const [pushing, setPushing]         = useState(false);
  const [testResult, setTestResult]   = useState(null);
  const apiConfigured = statusInfo?.api_key_configured;

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post('/cricclubs/test', { method: 'api' });
      setTestResult(data);
      toast[data.success ? 'success' : 'error'](data.success ? 'Connection successful' : data.error || 'Connection failed');
    } catch (err) { toast.error(err.response?.data?.error || 'Test failed'); }
    finally { setTesting(false); }
  };

  const pull = async () => {
    if (!ccSeriesId || !seriesId) return toast.error('Select both series');
    setPulling(true);
    try {
      await api.post('/cricclubs/pull', { method: 'api', cricclubs_series_id: ccSeriesId, local_series_id: seriesId });
      toast.success('Pull started — check History tab in a moment');
    } catch (err) { toast.error(err.response?.data?.error || 'Pull failed'); }
    finally { setPulling(false); }
  };

  const push = async () => {
    if (!seriesId) return toast.error('Select a local series');
    setPushing(true);
    try {
      await api.post('/cricclubs/push', { local_series_id: seriesId });
      toast.success('Fixtures pushed to CricClubs');
    } catch (err) { toast.error(err.response?.data?.error || 'Push failed'); }
    finally { setPushing(false); }
  };

  return (
    <div>
      {!apiConfigured && (
        <div style={{ padding: '14px 18px', background: 'rgba(245,166,35,.08)', border: '1px solid rgba(245,166,35,.3)', borderRadius: 8, marginBottom: 20, fontSize: 13, color: 'var(--mid)' }}>
          ⚠️ No API key configured. Go to the <strong>Settings</strong> tab to enter your CricClubs API key.
        </div>
      )}
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <SectionTitle>Test Connection</SectionTitle>
        <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 16 }}>Verify the API key works and CricClubs is reachable.</p>
        <button className="btn btn-primary" onClick={testConnection} disabled={testing || !apiConfigured}>
          {testing ? <><Spinner size={14} /> Testing…</> : '⚡ Test Connection'}
        </button>
        {testResult && (
          <div style={{ marginTop: 12, padding: '10px 14px', background: testResult.success ? 'rgba(0,200,150,.07)' : 'rgba(239,68,68,.07)', border: `1px solid ${testResult.success ? 'rgba(0,200,150,.3)' : 'rgba(239,68,68,.3)'}`, borderRadius: 8, fontSize: 13 }}>
            {testResult.success ? '✓ ' : '✗ '}{testResult.note || testResult.error || (testResult.success ? 'Connected' : 'Failed')}
          </div>
        )}
      </div>

      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <SectionTitle>Pull from CricClubs</SectionTitle>
        <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 16 }}>Pull teams and standings from CricClubs into your local database.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <Field label="Local Series">
            <select className="input select" value={seriesId} onChange={e => setSeriesId(e.target.value)}>
              <option value="">Select…</option>
              {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.season_year})</option>)}
            </select>
          </Field>
          <Field label="CricClubs Series ID">
            <input className="input" value={ccSeriesId} onChange={e => setCcSeriesId(e.target.value)}
              placeholder="e.g. GKA9Ah1JhtkHp5Dx8Fn_xw" style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 12 }} />
          </Field>
        </div>
        <button className="btn btn-primary" onClick={pull} disabled={pulling || !apiConfigured || !seriesId || !ccSeriesId}>
          {pulling ? <><Spinner size={14} /> Pulling…</> : '↓ Pull Now'}
        </button>
      </div>

      <div className="card" style={{ padding: 24 }}>
        <SectionTitle>Push to CricClubs</SectionTitle>
        <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 16 }}>Push confirmed fixtures from the portal to CricClubs.</p>
        <Field label="Local Series" note="Only confirmed fixtures will be pushed">
          <select className="input select" value={seriesId} onChange={e => setSeriesId(e.target.value)} style={{ width: 280 }}>
            <option value="">Select…</option>
            {seriesList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.season_year})</option>)}
          </select>
        </Field>
        <button className="btn btn-primary" onClick={push} disabled={pushing || !apiConfigured || !seriesId} style={{ marginTop: 8 }}>
          {pushing ? <><Spinner size={14} /> Pushing…</> : '↑ Push Fixtures'}
        </button>
      </div>
    </div>
  );
}

// ─── History Tab ──────────────────────────────────────────────────────────────
function HistoryTab({ syncHistory }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <SectionTitle>Sync & Import History</SectionTitle>
      {syncHistory.length === 0 ? (
        <EmptyState icon="📋" title="No history yet" description="Import a standings CSV or use API sync to see history here" />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Type</th><th>Method</th><th>Records</th><th>Status</th><th>Notes</th></tr></thead>
            <tbody>
              {syncHistory.map((h, i) => (
                <tr key={i}>
                  <td style={{ fontSize: 12 }}>{new Date(h.completed_at || h.started_at).toLocaleString()}</td>
                  <td style={{ fontSize: 11, textTransform: 'capitalize' }}>{h.sync_type}</td>
                  <td style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--mid)' }}>{h.method}</td>
                  <td style={{ fontWeight: 700 }}>{h.records_synced ?? '—'}</td>
                  <td><span style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', color: h.status === 'success' ? 'var(--accent)' : h.status === 'failed' ? 'var(--red)' : 'var(--gold)' }}>{h.status}</span></td>
                  <td style={{ fontSize: 11, color: 'var(--dim)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(() => { try { const e = JSON.parse(h.errors||'[]'); return e.length ? e.slice(0,2).join(', ') : '—'; } catch { return '—'; } })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────
function SettingsTab({ seriesList }) {
  const [config, setConfig]   = useState({ api_key_configured: false, api_key_masked: null, base_url: '', club_id: '' });
  const [apiKey, setApiKey]   = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [clubId, setClubId]   = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/cricclubs/config').then(r => {
      setConfig(r.data);
      setBaseUrl(r.data.base_url || '');
      setClubId(r.data.club_id || '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { base_url: baseUrl, club_id: clubId };
      if (apiKey) payload.api_key = apiKey;
      await api.post('/cricclubs/config', payload);
      toast.success('Settings saved');
      setApiKey(''); setShowKey(false);
      const r = await api.get('/cricclubs/config');
      setConfig(r.data);
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const clearKey = async () => {
    if (!window.confirm('Remove the API key?')) return;
    setSaving(true);
    try {
      await api.post('/cricclubs/config', { api_key: '' });
      toast.success('API key removed');
      const r = await api.get('/cricclubs/config');
      setConfig(r.data);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>;

  return (
    <div>
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <SectionTitle>CricClubs API Key</SectionTitle>
        <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 20 }}>
          Contact <strong>support@cricclubs.com</strong> to request API access for MichCA. Once received, paste the key below.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px', background: 'var(--surface)', borderRadius: 8, border: `1px solid ${config.api_key_configured ? 'rgba(0,200,150,.3)' : 'var(--border)'}`, marginBottom: 20 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: config.api_key_configured ? 'var(--accent)' : 'var(--dim)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>API Key</div>
            <div style={{ fontSize: 12, color: 'var(--mid)', fontFamily: "'Roboto Mono', monospace", marginTop: 2 }}>
              {config.api_key_configured ? config.api_key_masked : 'Not configured'}
            </div>
          </div>
          {config.api_key_configured && <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--red)' }} onClick={clearKey}>Remove</button>}
        </div>
        <Field label={config.api_key_configured ? 'Update API Key' : 'Enter API Key'} note="Leave blank to keep existing">
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input" type={showKey ? 'text' : 'password'} value={apiKey} onChange={e => setApiKey(e.target.value)}
              placeholder={config.api_key_configured ? 'Enter new key to replace…' : 'Paste your CricClubs API key…'}
              style={{ flex: 1, fontFamily: "'Roboto Mono', monospace" }} autoComplete="off" />
            <button className="btn btn-ghost" style={{ fontSize: 12, padding: '0 12px' }} onClick={() => setShowKey(p => !p)}>{showKey ? 'Hide' : 'Show'}</button>
          </div>
        </Field>
        <Field label="Club Base URL" note="Usually https://cricclubs.com/MichCA">
          <input className="input" value={baseUrl} onChange={e => setBaseUrl(e.target.value)} placeholder="https://cricclubs.com/MichCA" />
        </Field>
        <Field label="Club ID" note="Numeric club ID">
          <input className="input" value={clubId} onChange={e => setClubId(e.target.value)} placeholder="e.g. 2119" style={{ width: 160 }} />
        </Field>
        <button className="btn btn-primary" onClick={save} disabled={saving} style={{ marginTop: 8 }}>
          {saving ? <><Spinner size={14} /> Saving…</> : '✓ Save Settings'}
        </button>
      </div>

      <div className="card" style={{ padding: 24 }}>
        <SectionTitle>Series ID Mappings</SectionTitle>
        <p style={{ fontSize: 13, color: 'var(--mid)', marginBottom: 16 }}>Map local series to CricClubs series IDs for API sync.</p>
        {seriesList.map(s => <SeriesIdRow key={s.id} series={s} />)}
      </div>
    </div>
  );
}

function SeriesIdRow({ series }) {
  const [val, setVal]       = useState(series.cricclubs_id || '');
  const [dirty, setDirty]   = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/series/${series.id}`, { ...series, cricclubs_id: val });
      setDirty(false); toast.success('Saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
      <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{series.name}</div>
      <input className="input" style={{ width: 280, fontFamily: "'Roboto Mono', monospace", fontSize: 12 }}
        value={val} placeholder="CricClubs series ID"
        onChange={e => { setVal(e.target.value); setDirty(true); }} />
      {dirty && <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }} onClick={save} disabled={saving}>{saving ? <Spinner size={12} /> : 'Save'}</button>}
    </div>
  );
}
